import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CodeBracketIcon,
  LightBulbIcon,
  BookOpenIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'

const QuestionBankCategoryPage: React.FC = () => {
  const navigate = useNavigate()

  const handleCategorySelect = (collegeType: 'engineering' | 'management', tier: 'tier1' | 'tier2') => {
    navigate('/recruiter/question-bank/type', { 
      state: { category: { collegeType, tier } } 
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Question Bank</h1>
            <p className="text-gray-600 mt-2">
              Select category to manage questions
            </p>
          </div>
        </div>

        {/* Category Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Engineering Tier 1 */}
          <div 
            className="card p-6 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => handleCategorySelect('engineering', 'tier1')}
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-blue-100 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                <CodeBracketIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Engineering - Tier 1</h3>
                <p className="text-sm text-gray-600">Top-tier engineering colleges</p>
              </div>
            </div>
            <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
              <span>Select Category</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Engineering Tier 2 */}
          <div 
            className="card p-6 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => handleCategorySelect('engineering', 'tier2')}
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-blue-100 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                <CodeBracketIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Engineering - Tier 2</h3>
                <p className="text-sm text-gray-600">Other engineering colleges</p>
              </div>
            </div>
            <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
              <span>Select Category</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Management Tier 1 */}
          <div 
            className="card p-6 hover:border-green-300 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => handleCategorySelect('management', 'tier1')}
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-green-100 rounded-lg mr-4 group-hover:bg-green-200 transition-colors">
                <BuildingOfficeIcon className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Management - Tier 1</h3>
                <p className="text-sm text-gray-600">Top-tier B-schools</p>
              </div>
            </div>
            <div className="flex items-center text-green-600 font-medium group-hover:translate-x-1 transition-transform">
              <span>Select Category</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Management Tier 2 */}
          <div 
            className="card p-6 hover:border-green-300 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => handleCategorySelect('management', 'tier2')}
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-green-100 rounded-lg mr-4 group-hover:bg-green-200 transition-colors">
                <BuildingOfficeIcon className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Management - Tier 2</h3>
                <p className="text-sm text-gray-600">Other B-schools</p>
              </div>
            </div>
            <div className="flex items-center text-green-600 font-medium group-hover:translate-x-1 transition-transform">
              <span>Select Category</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuestionBankCategoryPage

